import { z } from "zod";
import { leadFormSubmissionRowSchema, leadFormsRowSchema } from "./schemas";

const Geometry: z.ZodType<GeoJSON.Geometry> = z.any();
const Properties: z.ZodType<GeoJSON.GeoJsonProperties> = z.record(z.any());

export const propertyTypeEnum = z.enum([
  "checkbox",
  "date",
  "datetime",
  "email",
  "hidden",
  "number",
  "password",
  "radio",
  "select",
  "text",
  "textarea",
  "time",
  "url",
  "media",
]);

const basePropertyOptions = z.object({
  id: z.string(),
  title: z.string(),
  default: z.string().optional(),
  description: z.string().optional(),
  placeholder: z.string().optional(),
  format: z.string().optional(),
  required: z.boolean().optional(),
});

const textPropertyOptions = basePropertyOptions.extend({
  type: z.literal("text"),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
});
const textareaPropertyOptions = basePropertyOptions.extend({
  type: z.literal("textarea"),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
});
const emailPropertyOptions = basePropertyOptions.extend({
  type: z.literal("email"),
});
const urlPropertyOptions = basePropertyOptions.extend({
  type: z.literal("url"),
});
const numberPropertyOptions = basePropertyOptions.extend({
  type: z.literal("number"),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
});
const datePropertyOptions = basePropertyOptions.extend({
  type: z.literal("date"),
});
const datetimePropertyOptions = basePropertyOptions.extend({
  type: z.literal("datetime"),
});
const timePropertyOptions = basePropertyOptions.extend({
  type: z.literal("time"),
});
const passwordPropertyOptions = basePropertyOptions.extend({
  type: z.literal("password"),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
});
const hiddenPropertyOptions = basePropertyOptions.extend({
  type: z.literal("hidden"),
});
const checkboxPropertyOptions = basePropertyOptions.extend({
  type: z.literal("checkbox"),
  options: z
    .array(z.string())
    .min(1, { message: "At least one option is required" }),
});
const radioPropertyOptions = basePropertyOptions.extend({
  type: z.literal("radio"),
  options: z
    .array(z.string())
    .min(1, { message: "At least one option is required" }),
});
const selectPropertyOptions = basePropertyOptions.extend({
  type: z.literal("select"),
  options: z
    .array(z.string())
    .min(1, { message: "At least one option is required" }),
});
const mediaPropertyOptions = basePropertyOptions.extend({
  type: z.literal("media"),
  accept: z.object({
    image: z.boolean().optional().default(true),
    video: z.boolean().optional().default(true),
  }),
});
const propertyTypes = z.discriminatedUnion("type", [
  textPropertyOptions,
  textareaPropertyOptions,
  numberPropertyOptions,
  emailPropertyOptions,
  urlPropertyOptions,
  datePropertyOptions,
  datetimePropertyOptions,
  timePropertyOptions,
  passwordPropertyOptions,
  hiddenPropertyOptions,
  checkboxPropertyOptions,
  radioPropertyOptions,
  selectPropertyOptions,
  mediaPropertyOptions,
]);
export type LeadFormProperty = z.infer<typeof propertyTypes>;

export const leadFormSchema = leadFormsRowSchema.omit({ schema: true }).extend({
  schema: z.object({
    submitButtonText: z.string().optional().default("Submit"),
    successMessage: z.string().optional().default("Thank you!"),
    properties: propertyTypes.array(),
    titleField: z.number().optional(),
    descriptionField: z.number().optional(),
    captureLocation: z.boolean().optional().default(false),
    captureCategory: z.boolean().optional().default(false),
    ctaText: z.string().optional().default("Contact"),
  }),
});
export type LeadForm = z.infer<typeof leadFormSchema>;

export const leadFormSubmissionSchema = leadFormSubmissionRowSchema
  .omit({ geometry: true, properties: true, data: true })
  .extend({
    geometry: Geometry.optional().nullable(),
    properties: Properties.and(
      z.object({
        center: z.tuple([z.number(), z.number()]),
      })
    )
      .optional()
      .nullable(),
    data: propertyTypes.and(z.object({ value: z.any() })).array(),
  });

export type LeadFormSubmission = z.infer<typeof leadFormSubmissionSchema>;
